import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Medal, EyeOff } from "lucide-react";
import { api } from "../lib/api";

const CATS = ["2017", "2018"];

const Pointeurs = () => {
  const [statsPublic, setStatsPublic] = useState(null); // null=loading

  useEffect(() => {
    api.get("/settings").then((r) => setStatsPublic(!!r.data.stats_public)).catch(() => setStatsPublic(false));
  }, []);

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="pointeurs-page">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-8 sm:py-20">
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
            Statistiques en direct
          </span>
          <h1 className="mt-2 sm:mt-3 font-display text-4xl sm:text-6xl lg:text-8xl uppercase leading-none">
            Meilleurs <span className="text-[#E60012]">pointeurs</span>
          </h1>
          <p className="mt-5 text-white/60 max-w-2xl text-sm sm:text-base">
            Top 10 par catégorie. Buts (B), passes (A), points (PTS = B + A), parties jouées (PJ)
            et minutes de pénalité (PIM).
          </p>
        </div>
      </section>

      {statsPublic === false ? (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <div className="bg-[#111] border border-white/10 p-10 sm:p-14 text-center" data-testid="pointeurs-hidden">
            <EyeOff className="w-10 h-10 text-[#E60012] mx-auto" />
            <h2 className="mt-5 font-display text-3xl sm:text-4xl uppercase">Statistiques à venir</h2>
            <p className="mt-4 text-white/60 max-w-xl mx-auto text-sm sm:text-base">
              Les statistiques individuelles (buts, passes, classement des pointeurs) seront
              publiées prochainement. Les alignements et les résultats restent disponibles sur la
              page de chaque partie.
            </p>
          </div>
        </section>
      ) : (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 space-y-14">
          {CATS.map((cat, i) => (
            <CategoryLeaderboard key={cat} category={cat} delay={i * 0.1} />
          ))}
        </section>
      )}
    </div>
  );
};

const CategoryLeaderboard = ({ category, delay }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api
      .get(`/top-scorers?category=${category}&limit=10`)
      .then((r) => !cancel && setRows(r.data))
      .catch(() => !cancel && setRows([]))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [category]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      data-testid={`pointeurs-cat-${category}`}
    >
      <div className="flex items-center gap-3 mb-5">
        <Trophy className="w-5 h-5 text-[#E60012]" />
        <h2 className="font-display text-3xl sm:text-4xl uppercase">Catégorie {category}</h2>
        <div className="flex-1 h-px bg-white/10" />
        <span className="font-condensed uppercase tracking-[0.2em] text-xs text-white/50">
          Top 10
        </span>
      </div>

      <div className="bg-[#111] border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="hidden md:grid grid-cols-[50px_60px_1fr_90px_40px_40px_50px_50px] gap-2 px-5 py-3 bg-black/40 text-[10px] font-condensed uppercase tracking-[0.2em] text-white/55">
          <div>#</div>
          <div className="text-center">N°</div>
          <div>Joueur</div>
          <div>Équipe</div>
          <div className="text-center">B</div>
          <div className="text-center">A</div>
          <div className="text-center">PTS</div>
          <div className="text-center">PIM</div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-white/40 font-condensed uppercase tracking-[0.2em] text-xs">
            Chargement…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-white/40 font-condensed uppercase tracking-[0.2em] text-xs">
            Aucun point enregistré pour l'instant.
          </div>
        ) : (
          rows.map((p, idx) => {
            const medal = idx === 0 ? "text-yellow-400" : idx === 1 ? "text-zinc-300" : idx === 2 ? "text-amber-700" : "text-white/55";
            return (
              <div
                key={p.player_id}
                className={`grid grid-cols-[50px_1fr_50px_50px] md:grid-cols-[50px_60px_1fr_90px_40px_40px_50px_50px] gap-2 items-center px-5 py-3.5 border-t border-white/5 ${idx % 2 === 0 ? "bg-[#0c0c0c]" : "bg-[#141414]"}`}
                data-testid={`pointeur-row-${p.player_id}`}
              >
                <div className={`flex items-center gap-1 font-display text-xl ${medal}`}>
                  {idx < 3 && <Medal className="w-4 h-4" />}
                  {idx + 1}
                </div>
                <div className="hidden md:block text-center font-display text-xl text-[#E60012]">
                  {p.number || "—"}
                </div>
                <div className="min-w-0">
                  <div className="font-condensed text-white truncate">{p.name}</div>
                  <div className="md:hidden text-[10px] text-white/45 font-condensed uppercase tracking-[0.15em] truncate">
                    #{p.number || "—"} · {p.team_name}
                  </div>
                </div>
                <div className="hidden md:block text-xs text-white/60 font-condensed uppercase tracking-[0.15em] truncate">
                  {p.team_name}
                </div>
                <div className="text-center font-display text-lg text-white md:block hidden">{p.goals}</div>
                <div className="text-center font-display text-lg text-white/70 md:block hidden">{p.assists}</div>
                <div className="text-center font-display text-xl text-[#E60012]">{p.points}</div>
                <div className="text-center md:block">
                  <span className="md:hidden text-[9px] text-white/40 font-condensed tracking-wider">PIM </span>
                  <span className="font-condensed text-sm text-white/55">{p.pim || "—"}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </motion.div>
  );
};

export default Pointeurs;
